<template>
  <div class="sf-wrap">
    <transition name="sf-slide" mode="out-in">

      <!-- ROOM CHOICE STATE -->
      <div v-if="state === 'room_choice'" key="room_choice">
        <div class="sf-hero sf-hero--plain">
          <div class="sf-hero-scrim"></div>
          <div class="sf-identity">
            <img v-if="getMerchant.image_url" :src="getMerchant.image_url" class="sf-identity-logo" :alt="getMerchant.name">
            <div v-else class="sf-identity-logo-fallback">
              <i class="simple-icon-home"></i>
            </div>
            <div class="sf-identity-body">
              <p class="sf-identity-name mb-0">{{ getMerchant.name }}</p>
            </div>
          </div>
        </div>
        <div class="sf-card sf-card--lifted mx-3 mb-5">
          <div class="sf-card-body text-center">
            <p class="sf-question mb-1">Would you like to include your room information?</p>
            <p class="sf-choice-hint mb-4">This helps us respond to your feedback more effectively.</p>
            <div class="sf-choice-row">
              <button class="sf-choice-btn sf-choice-good" @click="chooseWithRoom">
                <div class="sf-choice-icon-wrap sf-good-icon-bg">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#1A8F44" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <span class="sf-choice-label">With Room Info</span>
              </button>
              <button class="sf-choice-btn sf-choice-bad" @click="chooseAnonymous">
                <div class="sf-choice-icon-wrap sf-anon-icon-bg">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
                <span class="sf-choice-label">Anonymous</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ROOM SELECT STATE -->
      <div v-else-if="state === 'room_select'" key="room_select">
        <div class="sf-hero sf-hero--plain">
          <div class="sf-hero-scrim"></div>
          <div class="sf-identity">
            <img v-if="getMerchant.image_url" :src="getMerchant.image_url" class="sf-identity-logo" :alt="getMerchant.name">
            <div v-else class="sf-identity-logo-fallback">
              <i class="simple-icon-home"></i>
            </div>
            <div class="sf-identity-body">
              <p class="sf-identity-name mb-0">{{ getMerchant.name }}</p>
            </div>
          </div>
        </div>
        <div class="sf-card sf-card--lifted mx-3 mb-5">
          <div class="sf-card-body">
            <p class="sf-question mb-3">Select your room</p>
            <div class="sf-search-row mb-3">
              <input
                class="sf-search-input"
                type="text"
                v-model="room_keyword"
                placeholder="Search room..."
                @keyup.enter="searchRooms">
              <button class="sf-search-btn" @click="searchRooms">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
              </button>
            </div>
            <div v-if="rooms_loading" class="sf-rooms-empty">Loading rooms...</div>
            <div v-else-if="rooms_error" class="sf-error">{{ rooms_error }}</div>
            <div v-else-if="rooms.length === 0" class="sf-rooms-empty">No rooms found.</div>
            <div v-else class="sf-room-list">
              <button
                v-for="room in rooms"
                :key="room.id"
                class="sf-room-item"
                @click="selectRoom(room)">
                <div class="sf-room-icon-wrap">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <span class="sf-room-name">{{ room.name }}</span>
                <svg class="sf-platform-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ADBBC8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </button>
            </div>
            <button class="sf-back-btn mt-3" @click="state = 'room_choice'">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="19" y1="12" x2="5" y2="12"/>
                <polyline points="12 19 5 12 12 5"/>
              </svg>
              Back
            </button>
          </div>
        </div>
      </div>

      <!-- LANDING STATE -->
      <div v-else-if="state === 'landing'" key="landing">
        <div class="sf-hero" :class="{'sf-hero--plain': !form.image_url}">
          <img v-if="form.image_url" :src="form.image_url" alt="feedback-banner" class="sf-hero-img">
          <div class="sf-hero-scrim"></div>
          <div class="sf-identity">
            <img v-if="getMerchant.image_url" :src="getMerchant.image_url" class="sf-identity-logo" :alt="getMerchant.name">
            <div v-else class="sf-identity-logo-fallback">
              <i class="simple-icon-home"></i>
            </div>
            <div class="sf-identity-body">
              <p class="sf-identity-name mb-0">{{ getMerchant.name }}</p>
              <div class="sf-identity-meta">
                <span v-if="getMerchant.star" class="sf-identity-stars"><span v-for="i in getMerchant.star" :key="'f'+i" class="sf-star-fill">★</span><span v-for="i in emptyStars" :key="'e'+i" class="sf-star-empty">★</span></span>
                <span v-if="getMerchant.star && getMerchant.address" class="sf-dot-sep">·</span>
                <span v-if="getMerchant.address" class="sf-identity-addr">{{ getMerchant.address }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="sf-card sf-card--lifted mx-3 mb-5">
          <div class="sf-card-body text-center">
            <div v-if="source || room_name" class="sf-badges-row sf-badges-row--center mb-3">
              <div v-if="source" class="sf-touchpoint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span class="sf-touchpoint-label">{{ source }}</span>
              </div>
              <div v-if="room_name" class="sf-touchpoint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <span class="sf-touchpoint-label">{{ room_name }}</span>
              </div>
            </div>
            <p v-if="landing_config.question" class="sf-question">{{ landing_config.question }}</p>
            <div class="sf-divider my-4"></div>
            <div class="sf-choice-row">
              <button class="sf-choice-btn sf-choice-good" @click="chooseGood">
                <div class="sf-choice-icon-wrap sf-good-icon-bg">
                  <img v-if="good_config.choice_icon_url" :src="good_config.choice_icon_url" class="sf-choice-img" alt="good">
                  <img v-else :src="icon_happiness" class="sf-choice-img" alt="good">
                </div>
                <span class="sf-choice-label">{{ good_config.choice_label || 'Good' }}</span>
              </button>
              <button class="sf-choice-btn sf-choice-bad" @click="chooseBad">
                <div class="sf-choice-icon-wrap sf-bad-icon-bg">
                  <img v-if="bad_config.choice_icon_url" :src="bad_config.choice_icon_url" class="sf-choice-img" alt="bad">
                  <img v-else :src="icon_angry" class="sf-choice-img" alt="bad">
                </div>
                <span class="sf-choice-label">{{ bad_config.choice_label || 'Bad' }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- GOOD STATE -->
      <div v-else-if="state === 'good'" key="good">
        <div v-if="form.image_url" class="sf-banner-wrap">
          <img :src="form.image_url" alt="feedback-banner" class="sf-banner">
        </div>
        <div class="sf-card sf-card--good mx-3 mt-4 mb-5">
          <div class="sf-card-body">
            <div class="sf-badges-row mb-3">
              <div v-if="source" class="sf-touchpoint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span class="sf-touchpoint-label">{{ source }}</span>
              </div>
              <div v-if="room_name" class="sf-touchpoint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <span class="sf-touchpoint-label">{{ room_name }}</span>
              </div>
            </div>
            <p v-if="good_config.text" class="sf-body-text mb-4">{{ good_config.text }}</p>
            <div class="sf-platforms">
              <a
                v-for="option in sorted_review_options"
                :key="option.platform_name"
                class="sf-platform-btn"
                href="javascript:void(0)"
                @click="clickPlatform(option)">
                <div class="sf-platform-icon-wrap">
                  <img v-if="option.platform_icon_url" :src="option.platform_icon_url" class="sf-platform-icon" :alt="option.platform_name">
                  <div v-else class="sf-platform-icon-fallback">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8A9AB0" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                      <line x1="2" y1="12" x2="22" y2="12"/>
                      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                  </div>
                </div>
                <div class="sf-platform-info">
                  <span class="sf-platform-name">{{ option.platform_name }}</span>
                  <span class="sf-platform-cta">Write a review</span>
                </div>
                <svg class="sf-platform-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ADBBC8" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"/>
                  <polyline points="12 5 19 12 12 19"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      <!-- BAD STATE -->
      <div v-else-if="state === 'bad'" key="bad">
        <div v-if="form.image_url" class="sf-banner-wrap">
          <img :src="form.image_url" alt="feedback-banner" class="sf-banner">
        </div>
        <div class="sf-card sf-card--bad mx-3 mt-4 mb-5">
          <div class="sf-card-body">
            <div class="sf-badges-row mb-3">
              <div v-if="source" class="sf-touchpoint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                <span class="sf-touchpoint-label">{{ source }}</span>
              </div>
              <div v-if="room_name" class="sf-touchpoint">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                <span class="sf-touchpoint-label">{{ room_name }}</span>
              </div>
            </div>
            <p v-if="bad_config.text" class="sf-body-text mb-4">{{ bad_config.text }}</p>
            <p class="sf-field-label">Your message</p>
            <textarea
              class="sf-complaint"
              rows="5"
              v-model="complaint"
              :placeholder="bad_config.complaint_placeholder || 'Please share your experience with us...'">
            </textarea>
            <p class="sf-error mt-1" v-if="complaint_error">{{ complaint_error }}</p>
            <state-button :click="submitComplaint" variant="primary" block size="lg" class="mt-3">Send Feedback</state-button>
            <p v-if="bad_config.best_regards" class="sf-regards mt-4">{{ bad_config.best_regards }}</p>
          </div>
        </div>
      </div>

      <!-- THANK YOU STATE -->
      <div v-else key="thank_you" class="sf-thankyou">
        <div class="sf-thankyou-inner text-center px-4">
          <div class="sf-success-icon mx-auto">
            <svg class="sf-checkmark" viewBox="0 0 52 52" xmlns="http://www.w3.org/2000/svg">
              <circle cx="26" cy="26" r="25" fill="#1A8F44"/>
              <path class="sf-checkmark-check" d="M14 27l8 8 16-16" stroke="#fff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
            </svg>
          </div>
          <p class="sf-thankyou-title mt-5">Thank you!</p>
          <p class="sf-thankyou-sub mt-2">Your feedback has been received. We appreciate you taking the time to share your experience.</p>
          <div v-if="source || room_name" class="sf-badges-row sf-badges-row--center mt-4">
            <div v-if="source" class="sf-touchpoint">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              <span class="sf-touchpoint-label">{{ source }}</span>
            </div>
            <div v-if="room_name" class="sf-touchpoint">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#637181" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              <span class="sf-touchpoint-label">{{ room_name }}</span>
            </div>
          </div>
          <p v-if="bad_config.best_regards" class="sf-regards sf-regards--delayed mt-4">{{ bad_config.best_regards }}</p>
        </div>
      </div>

    </transition>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { get, post } from '@/services/index.js'
  import iconHappiness from '@/assets/img/icon/happiness.png'
  import iconAngry from '@/assets/img/icon/angry.png'

  export default {
    props: {
      form: {
        type: Object,
        default: function() { return {} }
      },
      feedback_id: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        state: 'landing',
        session_id: '',
        room_id: '',
        room_name: '',
        room_keyword: '',
        rooms: [],
        rooms_loading: false,
        rooms_error: '',
        source: '',
        complaint: '',
        complaint_error: '',
        icon_happiness: iconHappiness,
        icon_angry: iconAngry
      }
    },
    computed: {
      ...mapGetters(['getMerchant']),
      emptyStars() {
        var total = this.getMerchant && this.getMerchant.star ? this.getMerchant.star : 0
        return total < 5 ? 5 - total : 0
      },
      landing_config() {
        return (this.form && this.form.simple_landing_config) || {}
      },
      good_config() {
        return (this.form && this.form.simple_good_config) || {}
      },
      bad_config() {
        return (this.form && this.form.simple_bad_config) || {}
      },
      sorted_review_options() {
        let options = (this.good_config && this.good_config.review_options) || []
        return options.slice().sort(function(a, b) { return a.position - b.position })
      }
    },
    created() {
      this.session_id = this.$createUUID()
      let raw_source = this.$route && this.$route.query && this.$route.query.source
      if (raw_source) {
        try { this.source = decodeURIComponent(raw_source) } catch (e) { this.source = raw_source }
      }
      let raw_room_id = this.$route && this.$route.query && this.$route.query.room_id
      if (raw_room_id) {
        try { this.room_id = decodeURIComponent(raw_room_id) } catch (e) { this.room_id = raw_room_id }
      }
      if (!this.room_id) {
        this.state = 'room_choice'
      }
    },
    mounted() {
      this.postEvent('OPEN')
    },
    methods: {
      fetchRooms() {
        this.rooms_loading = true
        this.rooms_error = ''
        let params = { merchant_id: this.getMerchant.id }
        if (this.room_keyword.trim()) params.keyword = this.room_keyword.trim()
        get("CORE_HOTEL", "room-list", params).then((res) => {
          this.rooms = (res && res.data && res.data.data) || []
          this.rooms_loading = false
        }).catch(() => {
          this.rooms_error = 'Failed to load room list. Please try again.'
          this.rooms_loading = false
        })
      },
      chooseWithRoom() {
        this.fetchRooms()
        this.state = 'room_select'
      },
      chooseAnonymous() {
        this.state = 'landing'
      },
      selectRoom(room) {
        this.room_id = room.id
        this.room_name = room.name
        this.state = 'landing'
      },
      searchRooms() {
        this.fetchRooms()
      },
      postEvent(event_type, payload) {
        let body = {
          session_id: this.session_id,
          merchant_id: this.getMerchant.id,
          reservation_id: null,
          room_id: this.room_id || null,
          event_type: event_type
        }
        if (this.source) body.source = this.source
        if (payload) body.payload = payload
        return post("CORE_HOTEL", `feedback-form/${this.feedback_id}/event`, body)
      },
      chooseGood() {
        this.postEvent('CHOICE_GOOD')
        this.state = 'good'
      },
      chooseBad() {
        this.postEvent('CHOICE_BAD')
        this.state = 'bad'
      },
      clickPlatform(option) {
        this.postEvent('GOOD_REDIRECT', {
          platform_name: option.platform_name,
          platform_url: option.platform_url
        })
        window.open(option.platform_url, '_blank')
      },
      submitComplaint() {
        return new Promise((resolve, reject) => {
          if (this.complaint.trim() === '') {
            this.complaint_error = 'Please enter your feedback before submitting.'
            reject()
            return
          }
          this.complaint_error = ''
          this.postEvent('BAD_SUBMIT', { complaint: this.complaint }).then((res) => {
            if (res && res.data && res.data.success === false) {
              this.complaint_error = res.data.message || 'Failed to submit feedback. Please try again.'
              reject()
              return
            }
            this.state = 'thank_you'
            resolve()
          }).catch(() => {
            this.complaint_error = 'Failed to submit feedback. Please try again.'
            reject()
          })
        })
      }
    }
  }
</script>

<style scoped>
  /* ── Wrap ───────────────────────────────────────── */
  .sf-wrap {
    --sf-font:    Nunito-Regular,   sans-serif;
    --sf-font-sb: Nunito-SemiBold,  sans-serif;
    --sf-font-bd: Nunito-Bold,      sans-serif;
    --sf-font-xb: Nunito-ExtraBold, sans-serif;
    background: #F5F7FA;
    min-height: 100vh;
    padding-bottom: 32px;
    font-family: var(--sf-font);
    font-kerning: normal;
    text-rendering: optimizeLegibility;
  }

  /* ── Banner ─────────────────────────────────────── */
  .sf-banner-wrap {
    position: relative;
  }
  .sf-banner {
    width: 100%;
    max-height: 220px;
    object-fit: cover;
    display: block;
  }

  /* ── Card shell ─────────────────────────────────── */
  .sf-card {
    background: #fff;
    border-radius: 20px;
    border: 1px solid #E8EDF2;
    box-shadow: 0 2px 12px rgba(15, 30, 50, 0.06), 0 1px 3px rgba(15, 30, 50, 0.04);
    overflow: hidden;
  }
  .sf-card--good { border-top: 3px solid #1A8F44; }
  .sf-card--bad  { border-top: 3px solid #D97706; }

  .sf-card-body {
    padding: 24px 20px;
  }

  /* ── Hero ───────────────────────────────────────── */
  .sf-hero {
    position: relative;
    overflow: hidden;
    min-height: 190px;
  }
  .sf-hero--plain {
    background: linear-gradient(145deg, #19293a 0%, #2b4760 60%, #1e3a52 100%);
  }
  .sf-hero-img {
    width: 100%;
    height: 270px;
    object-fit: cover;
    display: block;
  }
  .sf-hero-scrim {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      rgba(6, 12, 20, 0.12) 42%,
      rgba(6, 12, 20, 0.70) 100%
    );
  }

  /* ── Hotel identity overlay ─────────────────────── */
  .sf-identity {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    align-items: flex-end;
    gap: 12px;
    padding: 0 20px 60px;
  }
  .sf-identity-logo {
    width: 54px;
    height: 54px;
    border-radius: 12px;
    object-fit: cover;
    border: 2px solid rgba(255, 255, 255, 0.88);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    flex-shrink: 0;
  }
  .sf-identity-logo-fallback {
    width: 54px;
    height: 54px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.14);
    border: 2px solid rgba(255, 255, 255, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(255, 255, 255, 0.88);
    font-size: 22px;
    flex-shrink: 0;
  }
  .sf-identity-body {
    min-width: 0;
    padding-bottom: 2px;
  }
  .sf-identity-name {
    font-size: 1.125rem;
    font-family: var(--sf-font-xb);
    color: #ffffff;
    text-shadow: 0 1px 8px rgba(0, 0, 0, 0.55);
    line-height: 1.25;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sf-identity-meta {
    display: flex;
    align-items: center;
    gap: 5px;
    margin-top: 5px;
    overflow: hidden;
  }
  .sf-identity-stars {
    display: inline-flex;
    gap: 1px;
    flex-shrink: 0;
  }
  .sf-star-fill {
    color: #FBD949;
    font-size: 0.6875rem;
    line-height: 1;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  .sf-star-empty {
    color: rgba(255, 255, 255, 0.32);
    font-size: 0.6875rem;
    line-height: 1;
  }
  .sf-dot-sep {
    color: rgba(255, 255, 255, 0.45);
    font-size: 0.6875rem;
    flex-shrink: 0;
    line-height: 1;
  }
  .sf-identity-addr {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.72);
    line-height: 1;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* ── Card lifted over hero ──────────────────────── */
  .sf-card--lifted {
    margin-top: -44px;
    position: relative;
    z-index: 1;
    border-radius: 24px 24px 20px 20px;
    box-shadow: 0 -6px 24px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.06);
  }

  /* ── Landing: title & question ──────────────────── */
  .sf-title {
    font-size: 1.375rem;
    font-family: var(--sf-font-xb);
    color: #1C2B36;
    margin-bottom: 0;
    line-height: 1.25;
    letter-spacing: -0.02em;
    text-wrap: balance;
  }
  .sf-question {
    font-size: 0.9375rem;
    color: #5D6878;
    margin-bottom: 0;
    line-height: 1.6;
    text-wrap: balance;
  }
  .sf-divider {
    height: 1px;
    background: #EEF2F6;
  }

  /* ── Choice buttons ─────────────────────────────── */
  .sf-choice-row {
    display: flex;
    gap: 12px;
  }
  .sf-choice-btn {
    flex: 1;
    background: #FAFBFC;
    border: 2px solid #E8EDF2;
    border-radius: 16px;
    padding: 20px 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
    text-align: center;
  }
  .sf-choice-btn:hover {
    transform: translateY(-3px);
  }
  .sf-choice-good:hover {
    border-color: #1A8F44;
    box-shadow: 0 6px 20px rgba(26, 143, 68, 0.14);
  }
  .sf-choice-bad:hover {
    border-color: #D97706;
    box-shadow: 0 6px 20px rgba(217, 119, 6, 0.14);
  }
  .sf-choice-btn:active {
    transform: scale(0.97) translateY(0);
  }

  .sf-choice-icon-wrap {
    width: 56px;
    height: 56px;
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1);
  }
  .sf-choice-btn:hover .sf-choice-icon-wrap {
    transform: scale(1.08);
  }
  .sf-good-icon-bg { background: #DCFCE7; }
  .sf-bad-icon-bg  { background: #FEF3C7; }

  .sf-choice-img {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }
  .sf-choice-label {
    font-size: 0.9375rem;
    font-family: var(--sf-font-bd);
    color: #1C2B36;
    line-height: 1.3;
  }
  .sf-choice-hint {
    font-size: 0.75rem;
    color: #637181;
    line-height: 1.4;
  }

  /* ── Body text (from config) ────────────────────── */
  .sf-body-text {
    font-size: 1rem;
    color: #4A5568;
    line-height: 1.65;
    margin-bottom: 0;
  }

  /* ── Platform list ──────────────────────────────── */
  .sf-platforms {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .sf-platform-btn {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #FAFBFC;
    border: 1px solid #E8EDF2;
    border-radius: 12px;
    padding: 13px 16px;
    text-decoration: none;
    color: #1C2B36;
    cursor: pointer;
    transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
  }
  .sf-platform-btn:hover {
    background: #F0F4F7;
    box-shadow: 0 2px 10px rgba(15, 30, 50, 0.07);
    transform: translateX(3px);
    text-decoration: none;
    color: #1C2B36;
  }
  .sf-platform-btn:active {
    transform: scale(0.98);
  }

  .sf-platform-icon-wrap {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    overflow: hidden;
    background: #EEF3F7;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sf-platform-icon {
    width: 36px;
    height: 36px;
    object-fit: contain;
  }
  .sf-platform-icon-fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }

  .sf-platform-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .sf-platform-name {
    font-size: 0.9375rem;
    font-family: var(--sf-font-bd);
    color: #1C2B36;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.4;
  }
  .sf-platform-cta {
    font-size: 0.75rem;
    color: #637181;
    line-height: 1.4;
  }
  .sf-platform-arrow {
    flex-shrink: 0;
    transition: transform 0.2s cubic-bezier(0.25, 1, 0.5, 1);
  }
  .sf-platform-btn:hover .sf-platform-arrow {
    transform: translateX(4px);
  }

  /* ── Bad state form ─────────────────────────────── */
  .sf-field-label {
    font-size: 0.8125rem;
    font-family: var(--sf-font-sb);
    color: #5D6878;
    margin-bottom: 8px;
    line-height: 1.4;
  }
  .sf-complaint {
    width: 100%;
    display: block;
    border-radius: 10px;
    font-size: 1rem;
    resize: none;
    border: 1.5px solid #E0E8EF;
    padding: 12px;
    color: #1C2B36;
    background: #FAFBFC;
    font-family: inherit;
    line-height: 1.5;
    transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  }
  .sf-complaint:focus {
    border-color: var(--merchant-primary-color);
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
    background: #fff;
  }
  .sf-error {
    font-size: 0.75rem;
    color: #DC2626;
    margin-bottom: 0;
    line-height: 1.4;
  }

  /* ── Touch point badge ──────────────────────────── */
  .sf-touchpoint {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    background: #EEF2F6;
    border-radius: 999px;
    padding: 4px 10px 4px 8px;
  }
  .sf-touchpoint-label {
    font-size: 0.75rem;
    color: #637181;
    line-height: 1.4;
    font-family: var(--sf-font-sb);
  }

  .sf-regards {
    font-size: 0.8125rem;
    color: #637181;
    text-align: center;
    margin-bottom: 0;
    line-height: 1.5;
  }

  /* ── Thank you state ────────────────────────────── */
  .sf-thankyou {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
  }
  .sf-thankyou-inner {
    width: 100%;
    max-width: 320px;
  }
  .sf-success-icon {
    width: 84px;
    height: 84px;
    animation: sf-icon-pop 0.5s cubic-bezier(0.25, 1, 0.5, 1) both;
  }
  .sf-checkmark {
    width: 84px;
    height: 84px;
    display: block;
  }
  .sf-checkmark-check {
    stroke-dasharray: 36;
    stroke-dashoffset: 36;
    animation: sf-check-draw 0.4s 0.35s ease-out forwards;
  }
  .sf-thankyou-title {
    font-size: 1.875rem;
    font-family: var(--sf-font-xb);
    color: #1C2B36;
    margin-bottom: 0;
    line-height: 1.2;
    letter-spacing: -0.025em;
    text-wrap: balance;
    animation: sf-fade-up 0.4s 0.3s ease-out both;
  }
  .sf-thankyou-sub {
    font-size: 0.9375rem;
    color: #5D6878;
    line-height: 1.65;
    margin-bottom: 0;
    animation: sf-fade-up 0.4s 0.4s ease-out both;
  }
  .sf-regards--delayed {
    animation: sf-fade-up 0.4s 0.5s ease-out both;
  }

  /* ── State transitions ──────────────────────────── */
  .sf-slide-enter-active {
    transition: opacity 0.25s ease, transform 0.25s cubic-bezier(0.25, 1, 0.5, 1);
  }
  .sf-slide-leave-active {
    transition: opacity 0.18s ease, transform 0.18s ease;
  }
  .sf-slide-enter {
    opacity: 0;
    transform: translateY(16px);
  }
  .sf-slide-leave-to {
    opacity: 0;
    transform: translateY(-8px);
  }

  /* ── Keyframes ──────────────────────────────────── */
  @keyframes sf-icon-pop {
    0%   { transform: scale(0.4); opacity: 0; }
    70%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); }
  }
  @keyframes sf-check-draw {
    to { stroke-dashoffset: 0; }
  }
  @keyframes sf-fade-up {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* ── Badges row ─────────────────────────────────── */
  .sf-badges-row {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .sf-badges-row--center {
    justify-content: center;
  }

  /* ── Anonymous icon bg ───────────────────────────── */
  .sf-anon-icon-bg { background: #F1F3F5; }

  /* ── Room select search ──────────────────────────── */
  .sf-search-row {
    display: flex;
    gap: 8px;
  }
  .sf-search-input {
    flex: 1;
    border-radius: 10px;
    font-size: 0.9375rem;
    border: 1.5px solid #E0E8EF;
    padding: 10px 12px;
    color: #1C2B36;
    background: #FAFBFC;
    font-family: inherit;
    line-height: 1.5;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    min-width: 0;
  }
  .sf-search-input:focus {
    border-color: var(--merchant-primary-color);
    outline: none;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
    background: #fff;
  }
  .sf-search-btn {
    flex-shrink: 0;
    background: #1A8F44;
    border: none;
    border-radius: 10px;
    color: #fff;
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s ease;
  }
  .sf-search-btn:hover { background: #157a39; }

  /* ── Room list ───────────────────────────────────── */
  .sf-room-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 280px;
    overflow-y: auto;
  }
  .sf-room-item {
    display: flex;
    align-items: center;
    gap: 12px;
    background: #FAFBFC;
    border: 1px solid #E8EDF2;
    border-radius: 10px;
    padding: 12px 14px;
    text-align: left;
    cursor: pointer;
    width: 100%;
    transition: background 0.15s ease, box-shadow 0.15s ease;
  }
  .sf-room-item:hover {
    background: #F0F4F7;
    box-shadow: 0 2px 8px rgba(15, 30, 50, 0.06);
  }
  .sf-room-icon-wrap {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    background: #EEF3F7;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .sf-room-name {
    flex: 1;
    font-size: 0.9375rem;
    font-family: var(--sf-font-sb);
    color: #1C2B36;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .sf-rooms-empty {
    font-size: 0.875rem;
    color: #8A9AB0;
    text-align: center;
    padding: 20px 0;
  }

  /* ── Back button ─────────────────────────────────── */
  .sf-back-btn {
    background: none;
    border: none;
    color: #637181;
    font-size: 0.8125rem;
    font-family: var(--sf-font-sb);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 0;
    line-height: 1.4;
  }
  .sf-back-btn:hover { color: #1C2B36; }

  /* ── Reduced motion ─────────────────────────────── */
  @media (prefers-reduced-motion: reduce) {
    .sf-slide-enter-active,
    .sf-slide-leave-active {
      transition: opacity 0.15s ease;
    }
    .sf-slide-enter,
    .sf-slide-leave-to {
      transform: none;
    }
    .sf-choice-btn,
    .sf-choice-icon-wrap,
    .sf-platform-btn,
    .sf-platform-arrow {
      transition: none;
    }
    .sf-choice-btn:hover,
    .sf-choice-icon-wrap:hover,
    .sf-platform-btn:hover {
      transform: none;
    }
    .sf-success-icon,
    .sf-thankyou-title,
    .sf-thankyou-sub,
    .sf-regards--delayed {
      animation: none;
      opacity: 1;
      transform: none;
    }
    .sf-checkmark-check {
      animation: none;
      stroke-dashoffset: 0;
    }
  }
</style>



// WEBPACK FOOTER //
// src/pages/general/feedback-v2/SimpleFeedbackForm.vue